import { WidgetStreamStreamingConfig } from 'widgets/streaming/WidgetStreamConfig';
import StreamDataService, {
    StreamDataHandlers,
} from 'streamsState/StreamDataService';
import { buildStreamHandlers } from 'widgets/store/stream/handlers';
import { selectChannelId } from 'streamsState/store/streamStatuses/selectors';
import {
    WidgetStreamManager,
    WidgetStreamManagerInfo,
    WidgetStreamManagerOptions,
} from 'widgets/streaming/widgetStreamManager/WidgetStreamManager';
import { ActionsDispatcher } from 'store/ActionsDispatcher';
import widgetStreamKey from 'widgets/streaming/widgetStreamManager/widgetStreamKey';

export interface WidgetStreamingStreamManagerOptions<TPayload, TDataMessage>
    extends WidgetStreamManagerOptions<TPayload, TDataMessage> {
    streamConfig: WidgetStreamStreamingConfig<TPayload, TDataMessage>;
    streamDataService: StreamDataService;
}

/*
 * Manages a widget stream (streaming)
 */
export class WidgetStreamingStreamManager<
    TPayload,
    TDataMessage = unknown,
> extends WidgetStreamManager<TPayload, TDataMessage> {
    public streamConfig: WidgetStreamStreamingConfig<TPayload, TDataMessage>;

    private streamDataService: StreamDataService;
    private isStreaming = false;
    private readonly handlers: StreamDataHandlers<TDataMessage>;

    public constructor(
        public options: WidgetStreamingStreamManagerOptions<
            TPayload,
            TDataMessage
        >
    ) {
        super(options);
        const {
            widgetType,
            widgetKey,
            loggingService,
            streamConfig,
            streamDataService,
            store,
        } = options;

        this.streamConfig = streamConfig;
        this.streamDataService = streamDataService;
        this.handlers = streamConfig.buildHandlers
            ? streamConfig.buildHandlers(widgetKey, store, loggingService)
            : buildStreamHandlers(
                  store,
                  widgetType,
                  widgetKey,
                  streamConfig.allowMultiStream
              );

        this.log('Initialised Streaming stream manager');
    }

    protected startStreaming(
        dispatcher: ActionsDispatcher,
        payload: TPayload,
        reason: string
    ) {
        this.log('Starting streaming', reason, payload);

        const state = this.store.getState();

        const { startChannelDestination, clearWidgetStreamData } =
            this.streamConfig;

        const streamKey = widgetStreamKey(
            this.widgetType,
            this.widgetKey,
            this.streamConfig.startChannelDestination
        );
        const currentChannelId = selectChannelId(state, streamKey);

        // Widget streams share channels across requests so we need to clear the stream before starting a new one...
        dispatcher.dispatch(clearWidgetStreamData(this.widgetKey));

        (async () => {
            await this.streamDataService.startAndWatchStream(
                streamKey,
                startChannelDestination,
                this.handlers,
                currentChannelId,
                dispatcher
            )(payload);

            this.isStreaming = true;
        })();
    }

    protected stopStreaming(dispatcher: ActionsDispatcher, reason: string) {
        if (!this.isStreaming) {
            return;
        }

        const state = this.store.getState();

        const {
            selectWidgetStreamKey,
            startChannelDestination,
            clearWidgetStreamData,
        } = this.streamConfig;

        const streamKey = selectWidgetStreamKey(state, this.widgetKey);
        if (!streamKey) {
            return;
        }

        const stopChannelDestination = startChannelDestination.replace(
            'start',
            'stop'
        );

        this.log('Stopping streaming ' + stopChannelDestination, reason);

        dispatcher.dispatch(clearWidgetStreamData(this.widgetKey));
        this.streamDataService.stopStream(streamKey, dispatcher);

        this.isStreaming = false;
    }

    public getInfo(): WidgetStreamManagerInfo<TPayload, TDataMessage> & {
        isStreaming: boolean;
    } {
        return {
            ...super.getInfo(),
            isStreaming: this.isStreaming,
        };
    }
}
